import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  type ReactNode,
  type RefCallback,
} from 'react';

// Viewport impression tracking shared by the home feed (post seen-state) and
// the Circles directory (recommendation impressions). A key counts once per
// mount after its element has been ≥50% visible for `dwellMs` continuous
// milliseconds while the page is visible (MRC viewability uses 1000; the
// feed's demotion signal uses 0 = first sighting). Counted keys batch to
// `send`: eagerly at FLUSH_BATCH, otherwise on a max-latency timer armed by
// the first pending key, and immediately when the page is hidden or
// unloaded. Fire-and-forget: a dropped batch only means a missing impression.

const FLUSH_MS = 2000;
const FLUSH_BATCH = 40; // eager-flush threshold; keeps bodies well under the 100-key server caps
const MAX_BATCH = 100; // server cap for both /posts/impressions and /recs/impressions
const VISIBLE_RATIO = 0.5;
// Browsers report the threshold crossing itself with float error (an entry
// fired for the 0.5 threshold can carry intersectionRatio 0.4999), so the
// visibility check admits a hair below the threshold it subscribed to.
const VISIBLE_RATIO_EPSILON = 1e-3;

export interface ImpressionEngineOptions {
  readonly send: (keys: string[]) => void;
  readonly dwellMs: number;
}

export interface ImpressionEngine {
  observe(el: Element, key: string): void;
  unobserve(el: Element): void;
  teardown(): void;
}

export function createImpressionEngine({ send, dwellMs }: ImpressionEngineOptions): ImpressionEngine {
  const hasDocument = typeof document !== 'undefined';
  const pending = new Set<string>();
  const seen = new Set<string>();
  const elToKey = new WeakMap<Element, string>();
  // Elements currently ≥50% in the viewport whose key has not counted yet.
  // Kept so a dwell interrupted by a hidden tab restarts when the tab returns:
  // IntersectionObserver does not re-fire on page visibility changes.
  const inView = new Set<Element>();
  const dwellTimers = new Map<Element, ReturnType<typeof setTimeout>>();
  let flushTimer: ReturnType<typeof setTimeout> | null = null;

  const pageHidden = () => hasDocument && document.visibilityState === 'hidden';

  const flush = () => {
    flushTimer = null;
    if (pending.size === 0) return;
    const batch = [...pending];
    pending.clear();
    for (let i = 0; i < batch.length; i += MAX_BATCH) {
      send(batch.slice(i, i + MAX_BATCH));
    }
  };

  const flushNow = () => {
    if (flushTimer !== null) {
      clearTimeout(flushTimer);
      flushTimer = null;
    }
    flush();
  };

  const scheduleFlush = () => {
    if (pending.size >= FLUSH_BATCH) {
      flushNow();
      return;
    }
    // Armed only when idle; not reset by later sightings, so a steady scroll
    // can't starve the beacon by perpetually deferring it.
    if (flushTimer === null) {
      flushTimer = setTimeout(flush, FLUSH_MS);
    }
  };

  const count = (key: string) => {
    if (seen.has(key)) return;
    seen.add(key);
    pending.add(key);
  };

  const cancelDwell = (el: Element) => {
    const timer = dwellTimers.get(el);
    if (timer === undefined) return;
    clearTimeout(timer);
    dwellTimers.delete(el);
  };

  const cancelAllDwells = () => {
    for (const timer of dwellTimers.values()) clearTimeout(timer);
    dwellTimers.clear();
  };

  const startDwell = (el: Element) => {
    const key = elToKey.get(el);
    if (key === undefined || seen.has(key) || dwellTimers.has(el)) return;
    dwellTimers.set(
      el,
      setTimeout(() => {
        dwellTimers.delete(el);
        inView.delete(el);
        count(key);
        scheduleFlush();
      }, dwellMs),
    );
  };

  const io = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        const target = entry.target;
        const key = elToKey.get(target);
        if (key === undefined || seen.has(key)) continue;
        const visible = entry.isIntersecting && entry.intersectionRatio >= VISIBLE_RATIO - VISIBLE_RATIO_EPSILON;
        if (!visible) {
          inView.delete(target);
          cancelDwell(target);
          continue;
        }
        if (dwellMs === 0) {
          count(key);
          continue;
        }
        inView.add(target);
        if (!pageHidden()) startDwell(target);
      }
      if (pending.size > 0) scheduleFlush();
    },
    { threshold: [VISIBLE_RATIO] },
  );

  // Hidden: a backgrounded tab is not viewable, so in-progress dwells stop,
  // and this may be the last chance to send before the page is discarded.
  const onVisibilityChange = () => {
    if (pageHidden()) {
      cancelAllDwells();
      flushNow();
      return;
    }
    for (const el of inView) startDwell(el);
  };
  if (hasDocument) {
    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('pagehide', flushNow);
  }

  return {
    observe: (el, key) => {
      elToKey.set(el, key);
      io.observe(el);
    },
    // IntersectionObserver holds a strong ref to every target until
    // unobserve/disconnect; releasing here lets a removed card be collected.
    // `seen` is kept: a card that scrolls out and back must not re-count.
    unobserve: (el) => {
      inView.delete(el);
      cancelDwell(el);
      io.unobserve(el);
    },
    teardown: () => {
      io.disconnect();
      inView.clear();
      cancelAllDwells();
      if (hasDocument) {
        document.removeEventListener('visibilitychange', onVisibilityChange);
        window.removeEventListener('pagehide', flushNow);
      }
      flushNow();
    },
  };
}

// Returns an unobserve cleanup for the registered element, or nothing when
// there is no element — useImpressionRef returns it from its ref callback so
// the element is released on detach.
type ObserveFn = (el: Element | null, key: string) => (() => void) | void;
const ImpressionCtx = createContext<ObserveFn | null>(null);

/** Tracks every `<TrackedImpression>` below it with one engine. `dwellMs` is
 *  read once, when the first tracked child registers. */
export function ImpressionProvider({
  send,
  dwellMs,
  children,
}: {
  send: (keys: string[]) => void;
  dwellMs: number;
  children: ReactNode;
}) {
  // The engine outlives the render that created it; read the latest `send`.
  const sendRef = useRef(send);
  sendRef.current = send;
  const engineRef = useRef<ImpressionEngine | null>(null);

  useEffect(() => {
    return () => {
      engineRef.current?.teardown();
      engineRef.current = null;
    };
  }, []);

  // Built lazily on the first registration: child effects run before the
  // parent's, so an effect-created engine would miss the first cards.
  const observe = useCallback<ObserveFn>(
    (el, key) => {
      if (el === null) return;
      const engine = (engineRef.current ??= createImpressionEngine({
        send: (keys) => sendRef.current(keys),
        dwellMs,
      }));
      engine.observe(el, key);
      return () => engine.unobserve(el);
    },
    [dwellMs],
  );

  return <ImpressionCtx.Provider value={observe}>{children}</ImpressionCtx.Provider>;
}

/** Ref callback that registers the element it is attached to under
 *  `impressionKey` with the nearest `<ImpressionProvider>` and releases it on
 *  detach. Use it to track a component's own root without a wrapper box (a
 *  wrapper would stop a grid card from stretching to its row). A null key, or
 *  no provider above, tracks nothing. */
export function useImpressionRef(impressionKey: string | null): RefCallback<Element> {
  const observe = useContext(ImpressionCtx);
  return useCallback<RefCallback<Element>>(
    (el) => {
      if (observe === null || impressionKey === null) return;
      return observe(el, impressionKey);
    },
    [observe, impressionKey],
  );
}

/** Wraps children in a box tracked under `impressionKey`. */
export function TrackedImpression({ impressionKey, children }: { impressionKey: string; children: ReactNode }) {
  const ref = useImpressionRef(impressionKey);
  return <div ref={ref}>{children}</div>;
}
